import { extractCountryCodeFromPhoneNumber } from "@ews-admin/global-design-system";

/**
 * How many digits a subscriber number has, per country the platform operates
 * in. Senegal is 9 (77 123 45 67), Chad is 8 (66 12 34 56).
 */
const NATIONAL_NUMBER_LENGTH: Record<string, number> = {
  "+221": 9,
  "+235": 8,
};

/** Digits only, so spacing a number does not change whether it is valid. */
const digitsOf = (value: string) => value.replace(/\D/g, "");

/**
 * Whether a phone number has the right number of digits for its country.
 *
 * MED-1543 — the doctor invitation form accepted "+22178000012299", which is
 * two digits too long for Senegal, because it only ever checked the field was
 * not empty. The rules already in the codebase do not catch it either: the
 * schema's own regex allows anything from 6 to 20 characters, and the design
 * system's `isValidPhoneNumber` passes it too. An invitation goes out by SMS,
 * so a number that cannot exist is an invitation nobody ever receives.
 *
 * A country the platform does not serve yet has no rule here, so it is
 * accepted on length alone rather than refused for a shape we cannot judge.
 */
export function isPhoneNumberValidForCountry(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;

  const { countryCode, cleanedPhoneNumber } =
    extractCountryCodeFromPhoneNumber(trimmed);
  const digits = digitsOf(cleanedPhoneNumber);
  const expected = NATIONAL_NUMBER_LENGTH[countryCode];

  if (expected === undefined) return digits.length >= 6 && digits.length <= 15;
  return digits.length === expected;
}
